import { useState, type ReactNode } from 'react';
import { ExternalLink, Pencil, Settings, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { ConfirmDialog } from './ConfirmDialog';

/**
 * Standardizovaná sada akčních tlačítek pro řádky v seznamech.
 *
 * Dvě varianty:
 *
 * 1. <InlineActions.Row> — kompaktní icon-only tlačítka (hover group, na hover odkryjí)
 * 2. <InlineActions.IconBtn> — jedno standalone icon tlačítko
 */

interface IconBtnProps {
  icon: ReactNode;
  title: string;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  variant?: 'default' | 'danger' | 'primary';
  className?: string;
}

const BTN_VARIANTS = {
  default: 'text-gray-500 hover:text-white hover:bg-gray-700',
  danger: 'text-gray-500 hover:text-red-400 hover:bg-red-900/30',
  primary: 'text-gray-500 hover:text-plex hover:bg-plex/15',
};

function IconBtn({ icon, title, onClick, disabled, variant = 'default', className }: IconBtnProps) {
  return (
    <button
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'p-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
        BTN_VARIANTS[variant],
        className,
      )}
    >
      {icon}
    </button>
  );
}

interface RowProps {
  onSettings?: (e: React.MouseEvent) => void;
  onEdit?: (e: React.MouseEvent) => void;
  onOpen?: (e: React.MouseEvent) => void;
  onDelete?: (e: React.MouseEvent) => void;
  /** Potvrdit smazání — true = default text, string = vlastní text, false = bez potvrzení */
  confirmDelete?: string | boolean;
  disabled?: boolean;
  /** Vždy viditelné (bez group-hover) — výchozí false */
  visible?: boolean;
  children?: ReactNode;
  className?: string;
}

function Row({
  onSettings,
  onEdit,
  onOpen,
  onDelete,
  confirmDelete = true,
  disabled,
  visible = false,
  children,
  className,
}: RowProps) {
  const { t } = useTranslation();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirmDelete) {
      onDelete?.(e);
      return;
    }
    setDeleteOpen(true);
  };

  const confirmMsg = typeof confirmDelete === 'string'
    ? confirmDelete
    : t('ui.deleteItemConfirm');

  return (
    <>
      <div
        className={cn(
          'flex items-center gap-0.5',
          !visible && 'opacity-0 group-hover:opacity-100 transition-opacity',
          className,
        )}
      >
        {children}
        {onOpen && (
          <IconBtn
            icon={<ExternalLink size={13} />}
            title={t('ui.open')}
            onClick={(e) => {
              e.stopPropagation();
              onOpen(e);
            }}
            disabled={disabled}
          />
        )}
        {onSettings && (
          <IconBtn
            icon={<Settings size={13} />}
            title={t('ui.settings')}
            onClick={(e) => {
              e.stopPropagation();
              onSettings(e);
            }}
            disabled={disabled}
          />
        )}
        {onEdit && (
          <IconBtn
            icon={<Pencil size={13} />}
            title={t('ui.edit')}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(e);
            }}
            disabled={disabled}
          />
        )}
        {onDelete && (
          <IconBtn
            icon={<Trash2 size={13} />}
            title={t('ui.delete')}
            onClick={handleDeleteClick}
            disabled={disabled}
            variant='danger'
          />
        )}
      </div>

      {onDelete && confirmDelete !== false && (
        <ConfirmDialog
          open={deleteOpen}
          title={t('ui.deleteItemTitle')}
          description={confirmMsg}
          confirmLabel={t('ui.delete')}
          cancelLabel={t('ui.cancel')}
          danger
          onConfirm={() => {
            setDeleteOpen(false);
            // Synthetic event — call sites rarely rely on event fields
            onDelete({ stopPropagation() {}, preventDefault() {} } as React.MouseEvent);
          }}
          onCancel={() => setDeleteOpen(false)}
        />
      )}
    </>
  );
}

export const InlineActions = { Row, IconBtn };
